/**
 * User Management Component
 * Lists, searches, and manages user accounts
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { firebaseAdminAPI as adminAPI } from './firebaseAdminService';
import { User } from './mockData';

export const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleStatusChange = async (userId: string, newStatus: 'active' | 'banned' | 'suspended') => {
    if (!confirm(`Are you sure you want to ${newStatus} this user?`)) return;
    
    try {
      const success = await adminAPI.updateUserStatus(userId, newStatus);
      if (success) {
        await loadUsers();
        alert(`User status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Error updating user status');
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!confirm('Reset password for this user? A temporary password will be generated.')) return;
    
    try {
      const result = await adminAPI.resetPassword(userId);
      if (result.success && result.newPassword) {
        alert(`Password reset successful. New temporary password: ${result.newPassword}`);
      } else {
        alert('Error resetting password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'banned': return '#dc3545';
      case 'suspended': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const getMembershipColor = (type: string) => {
    switch (type) {
      case 'premium': return '#667eea';
      case 'enterprise': return '#764ba2';
      default: return '#6c757d';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      position: 'relative'
    }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#ffffff',
        padding: '20px 40px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <Link
              to="/x9s4k-portal-72/admin"
              style={{
                color: '#ffffff',
                textDecoration: 'none',
                fontSize: '1.25rem',
                fontWeight: 600
              }}
            >
              ← Dashboard
            </Link>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              margin: 0
            }}>
              User Management
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        padding: '40px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Search Bar */}
        <div style={{
          background: '#ffffff',
          padding: '20px 30px',
          borderRadius: '15px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          marginBottom: '30px'
        }}>
          <input
            type="text"
            placeholder="Search by username, email, or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 20px',
              border: '2px solid #e9ecef',
              borderRadius: '50px',
              fontSize: '1rem',
              outline: 'none',
              transition: 'border-color 0.3s'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#667eea';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e9ecef';
            }}
          />
        </div>

        {/* Users Table */}
        <div style={{
          background: '#ffffff',
          borderRadius: '15px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#6c757d' }}>
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#6c757d' }}>
              No users found
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{
                    background: '#f8f9fa',
                    borderBottom: '2px solid #e9ecef'
                  }}>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>User</th>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Role</th>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Status</th>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Membership</th>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Last Login</th>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      style={{
                        borderBottom: '1px solid #e9ecef',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#f8f9fa';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = '#ffffff';
                      }}
                    >
                      <td style={{ padding: '15px' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: '#1a1a1a', marginBottom: '4px' }}>
                            {user.fullName}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                            @{user.username} • {user.email}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '50px',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          background: user.role === 'admin' ? '#764ba2' : user.role === 'premium' ? '#667eea' : '#e9ecef',
                          color: user.role === 'admin' || user.role === 'premium' ? '#ffffff' : '#495057'
                        }}>
                          {user.role}
                        </span>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '50px',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          background: getStatusColor(user.status) + '20',
                          color: getStatusColor(user.status)
                        }}>
                          {user.status}
                        </span>
                      </td>
                      <td style={{ padding: '15px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '50px',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          background: getMembershipColor(user.membershipType) + '20',
                          color: getMembershipColor(user.membershipType)
                        }}>
                          {user.membershipType}
                        </span>
                      </td>
                      <td style={{ padding: '15px', color: '#6c757d', fontSize: '0.875rem' }}>
                        {new Date(user.lastLogin).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleViewUser(user)}
                            style={{
                              padding: '6px 12px',
                              background: '#667eea',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '50px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = '#5568d3';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = '#667eea';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            View
                          </button>
                          {user.status === 'active' ? (
                            <button
                              onClick={() => handleStatusChange(user.id, 'banned')}
                              style={{
                                padding: '6px 12px',
                                background: '#dc3545',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '50px',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = '#c82333';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = '#dc3545';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              Ban
                            </button>
                          ) : (
                            <button
                              onClick={() => handleStatusChange(user.id, 'active')}
                              style={{
                                padding: '6px 12px',
                                background: '#28a745',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '50px',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = '#218838';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = '#28a745';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              Unban
                            </button>
                          )}
                          <button
                            onClick={() => handleResetPassword(user.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#ffc107',
                              color: '#1a1a1a',
                              border: 'none',
                              borderRadius: '50px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = '#e0a800';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = '#ffc107';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            Reset PW
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* User Count */}
        <div style={{
          marginTop: '20px',
          textAlign: 'center',
          color: '#6c757d',
          fontSize: '0.875rem'
        }}>
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </main>

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowUserModal(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowUserModal(false)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'none',
                border: 'none',
                fontSize: '2rem',
                cursor: 'pointer',
                color: '#6c757d',
                lineHeight: 1
              }}
            >
              ×
            </button>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: 800,
              marginBottom: '20px',
              color: '#1a1a1a'
            }}>
              User Profile
            </h2>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <strong style={{ color: '#667eea' }}>Full Name:</strong>
                <div style={{ marginTop: '5px' }}>{selectedUser.fullName}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Username:</strong>
                <div style={{ marginTop: '5px' }}>@{selectedUser.username}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Email:</strong>
                <div style={{ marginTop: '5px' }}>{selectedUser.email}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Role:</strong>
                <div style={{ marginTop: '5px' }}>{selectedUser.role}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Status:</strong>
                <div style={{ marginTop: '5px' }}>{selectedUser.status}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Membership:</strong>
                <div style={{ marginTop: '5px' }}>{selectedUser.membershipType}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Platform:</strong>
                <div style={{ marginTop: '5px' }}>{selectedUser.platform}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>App Version:</strong>
                <div style={{ marginTop: '5px' }}>{selectedUser.appVersion}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Total Sessions:</strong>
                <div style={{ marginTop: '5px' }}>{selectedUser.totalSessions}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Avg Session Time:</strong>
                <div style={{ marginTop: '5px' }}>{selectedUser.averageSessionTime} minutes</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Created:</strong>
                <div style={{ marginTop: '5px' }}>{new Date(selectedUser.createdAt).toLocaleString()}</div>
              </div>
              <div>
                <strong style={{ color: '#667eea' }}>Last Login:</strong>
                <div style={{ marginTop: '5px' }}>{new Date(selectedUser.lastLogin).toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

